'use client';

import { canOpenPath } from '@/shared/components/route-permissions';
import { usePermissions } from '@/features/auth/hooks/use-permissions';
import {
  LayoutDashboard,
  FileText,
  Users,
  Package,
  Warehouse,
  Calculator,
  BarChart3,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  ChevronDown,
  RefreshCw,
  X,
  Truck,
  Building2,
  Car,
  FileSignature,
  ScrollText,
  FileSearch,
  Hash,
  Camera,
  Landmark,
  UserCheck,
  UserCog,
  ShieldCheck,
  Percent,
  FolderOpen,
  ArrowLeftRight,
  ClipboardList,
  FileSpreadsheet,
  Heart,
  CalendarX,
  Puzzle,
  Receipt,
  ReceiptText,
  Monitor,
  Code,
  Banknote,
  CreditCard,
  TrendingUp,
  ArrowUpFromLine,
  TrendingDown,
  ArrowDownToLine,
  Wallet,
  ClipboardCheck,
  ShoppingCart,
  ShoppingBag,
  Plug,
  Tags,
  Search,
  Pin,
  PinOff,
  PackageMinus,
  Recycle,
  Upload,
  Plus,
  FilePlus,
  PackagePlus,
  UtensilsCrossed,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition } from '@/shared/hooks/use-anchored-position';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { createContext, useContext, useState, useCallback, useEffect, useRef, type ElementType } from 'react';

import { cn } from '@softfactura/ui';
import { NavLeafItem } from '@/shared/components/layout/nav-leaf-item';
import { BrandLogoIcon } from '@/shared/components/brand-logo-icon';
import { BrandLogoFull } from '@/shared/components/brand-logo-full';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { useSubscriptionStatus } from '@/features/billing/hooks/use-billing';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/services/api';
import { useSidebarPrefsStore } from '@/shared/stores/sidebar-prefs-store';
import { useSidebarGroupsStore, selectEffectiveOpenGroup } from '@/shared/stores/sidebar-groups-store';
import { EfacturaIcon } from '@/shared/components/icons/efactura-icon';
import { SalesIcon } from '@/shared/components/icons/sales-icon';
import { PurchasesIcon } from '@/shared/components/icons/purchases-icon';
import { AccountingIcon } from '@/shared/components/icons/accounting-icon';
import { SidebarSupportButton } from '@/features/support/components/sidebar-support-button';
import { ALL_NAV_ITEMS as SETTINGS_NAV_ITEMS } from '@/features/settings/components/settings-nav';
import { NOMENCL_ITEMS } from '@/features/nomenclatoare/components/nomenclatoare-nav';
import { useActivityType } from '@/features/settings/hooks/use-activity-type';
import { useCompany } from '@/features/settings/hooks/use-settings';
import { useMarketplaceSettings, useMarketplaceUninvoicedCounts } from '@/features/marketplace-orders/hooks/use-marketplace';
import { navCountLabel, uninvoicedNavCounts, uninvoicedOrdersText } from '@/features/marketplace-orders/uninvoiced-nav';

// Hook: check if current user has access to any warehouse.
// Bypass is permission-based (`warehouses.bypass`) so custom roles can be
// configured to skip the per-warehouse assignment list — same way the
// built-in owner/admin/accountant defaults work.
//
// Uses the `/team/me/warehouses` self-endpoint (no team.read required) so
// it works for operators, viewers, and custom roles without team management
// permissions.
function useUserHasWarehouseAccess(): { loading: boolean; hasAccess: boolean } {
  const user = useAuthStore((s) => s.user);
  const permissions = user?.permissions ?? [];
  const isBypass = !!user && permissions.includes('warehouses.bypass');
  const { data, isLoading } = useQuery({
    queryKey: ['my-warehouse-access', user?.id],
    queryFn: () => api.get<{ bypass: boolean; warehouseIds: string[] }>('/team/me/warehouses'),
    enabled: !!user && !isBypass,
    staleTime: 60000,
  });
  if (!user) return { loading: false, hasAccess: false };
  if (isBypass) return { loading: false, hasAccess: true };
  return { loading: isLoading, hasAccess: !!data?.bypass || (data?.warehouseIds?.length || 0) > 0 };
}


// ── Types ──────────────────────────────────────────

interface NavBadgeSpec {
  /** Etichetă text — folosită ca fallback când nu există icon. */
  text: string;
  tone?: 'neutral' | 'green' | 'amber' | 'red';
  /**
   * Componentă opțională care înlocuiește text-ul. Folosită pentru branding
   * de flux (ex: pictogramă ANAF e-Factura). Tone-ul tot se aplică ca
   * background, dar fără border + tracking text. `text` rămâne folosit
   * pentru `aria-label`.
   */
  icon?: ElementType;
}

interface NavItem {
  name: string;
  href: string; // may include `?query=value` to disambiguate two items that share a route
  icon: ElementType;
  module?: string; // module code for paywall — shows PRO badge if user doesnt have it
  badge?: NavBadgeSpec; // optional pill displayed after the label (e.g. "Nou", "ANAF")
  // Permission gating — link e ASCUNS complet dacă user nu are permisiunea.
  // Diferit de `module` (care arată badge PRO + redirect upgrade); permisiunea
  // e binară: ai/n-ai (ex. accountant vede „Rotație stoc", operator nu).
  permission?: string;
  // CTA — render distinct ca buton, hierarchy în 3 nivele:
  //   'primary'   = full bg primary (accent solid, dominant — Document nou)
  //   'secondary' = outline primary (border + text primary, bg alb — Cheltuială)
  //   'tertiary'  = outline neutral (border + text neutral, bg alb — Recepție)
  cta?: 'primary' | 'secondary' | 'tertiary';
  // Backwards compat: vechiul flag boolean (deprecated, mapează la cta='primary').
  primary?: boolean;
  // Expandable item — chevron clickable separat de label; sub-items în dropdown
  expandable?: boolean;
  children?: NavItem[];
  // Visible doar pe mobile/tablet (< lg = 1024px). Pe desktop e ascuns complet
  // din sidebar (folosit pentru pagini optimizate tactil, ex. /m/scanner).
  mobileOnly?: boolean;
  // Ascuns până la conectarea contului de marketplace cu acest cod (ex. 'fashiondays').
  // Item-ul apare în bara laterală DOAR dacă platforma respectivă e conectată.
  requiresConnectedMarketplace?: string;
}

interface NavGroup {
  name: string;
  icon: ElementType;
  children: NavItem[];
  defaultOpen?: boolean;
  module?: string;
}

type NavigationEntry = NavItem | NavGroup;

function isNavGroup(entry: NavigationEntry): entry is NavGroup {
  return 'children' in entry;
}

// ── Tour anchors ──────────────────────────────────
// Maps nav item href → data-tour attribute used by the product tour spotlight.
// Returns undefined for non-tour items so attribute is not rendered.
function tourAnchorFor(href: string): string | undefined {
  if (href === '/facturi') return 'sidebar-invoices';
  if (href === '/clienti') return 'sidebar-clients';
  if (href === '/produse') return 'sidebar-products';
  return undefined;
}

// Anchor pe ANTETUL grupului (mereu vizibil, poziție stabilă indiferent de open/collapse)
// — folosit de tur pentru pași ale căror ținte-copil s-ar ascunde când grupul e închis
// (ex. „Produse" în „Vânzări"). Antetul nu se colapsează → spotlight-ul nu poate pica pe alt item.
function groupTourAnchor(name: string): string | undefined {
  if (name === 'Vânzări') return 'sidebar-group-vanzari';
  return undefined;
}


// ── Context ────────────────────────────────────────

interface SidebarContextType {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
}

export const SidebarContext = createContext<SidebarContextType>({
  isOpen: false,
  toggle: () => {},
  close: () => {},
});

export function useSidebar() {
  return useContext(SidebarContext);
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <SidebarContext.Provider value={{ isOpen, toggle, close }}>
      {children}
    </SidebarContext.Provider>
  );
}

// ── Navigation Structure ───────────────────────────

const navigation: NavigationEntry[] = [
  // Dashboard mutat pe linia search trigger (icon stânga, deasupra navigării).
  // dreptul lor vine din harta adreselor (ROUTE_PERMISSIONS, vezi hasPermFor)
  { name: 'Document nou', href: '/facturi/nou', icon: FilePlus, cta: 'primary' },
  { name: 'Cheltuială nouă', href: '/cheltuieli/nou', icon: Wallet, cta: 'secondary' },
  { name: 'Recepție nouă', href: '/receptie/nou', icon: PackagePlus, cta: 'tertiary', module: 'stock' },
  {
    name: 'Vânzări',
    // Pictograma custom „bar chart cu săgeată up" — metaforă vânzări în creștere
    // (vs generic ArrowUpFromLine care semnifica doar „outgoing").
    icon: SalesIcon,
    defaultOpen: true,
    children: [
      // Documente emise — promovate la primul nivel (eliminat sub-grupul
      // expandable „Documente Emise" — anti-pattern 3rd-level nesting per
      // NN/G progressive disclosure). Iconuri distincte per tip pentru
      // scan vizual rapid (toate erau FileText înainte → ne-distinctive).
      { name: 'Facturi',  href: '/facturi',  icon: FileText },
      { name: 'Proforme', href: '/proforme', icon: FileSignature, permission: 'invoices.read' },
      { name: 'Avize',    href: '/avize',    icon: ScrollText, permission: 'invoices.read' },
      { name: 'Oferte',   href: '/oferte',   icon: FileSearch, permission: 'invoices.read' },
      { name: 'Facturi recurente', href: '/facturi-recurente', icon: RefreshCw, module: 'invoicing_pro', permission: 'invoices.read' },
      // Facturarea comenzilor eMAG Marketplace (decizie user 2026-06-12: pagină
      // separată de Reconciliere, în grupul Vânzări, gated pe invoicing_pro)
      { name: 'Comenzi eMAG', href: '/comenzi-emag', icon: ShoppingBag, module: 'invoicing_pro', permission: 'invoices.read', requiresConnectedMarketplace: 'emag' },
      { name: 'Comenzi Fashion Days', href: '/comenzi-fashion-days', icon: ShoppingBag, module: 'invoicing_pro', permission: 'invoices.read', requiresConnectedMarketplace: 'fashiondays' },
      { name: 'Comenzi Trendyol', href: '/comenzi-trendyol', icon: ShoppingBag, module: 'invoicing_pro', permission: 'invoices.read', requiresConnectedMarketplace: 'trendyol' },
      { name: 'Încasări', href: '/incasari', icon: Banknote, permission: 'payments.read' },
      { name: 'Borderouri curieri', href: '/borderou-curier', icon: FileSpreadsheet, module: 'couriers', permission: 'couriers.read' },
      // Catalog produse/servicii — și în Setări → Produse (același ProduseListView); aici e
      // scurtătura din meniul principal, lângă Clienți (celălalt nomenclator ales la facturare).
      { name: 'Produse', href: '/produse', icon: Tags, permission: 'products.read' },
      { name: 'Clienți', href: '/clienti', icon: Users, permission: 'clients.read' },
      // „Import clienți & produse" mutat în Setări → Date (setup task, nu daily workflow).
    ],
  },
  {
    name: 'Achiziții',
    // Pictograma custom „bar chart cu săgeată down" — mirror simetric cu
    // SalesIcon pentru paritate vizuală între grupurile peer Vânzări/Achiziții.
    icon: PurchasesIcon,
    defaultOpen: true,
    children: [
      // Ordine pe fluxul de achiziție: NIR (folosit des) -> comandă -> factură -> cheltuială -> plată; directorul Furnizori ultimul (ca Clienți la Vânzări).
      { name: 'Recepții (NIR)', href: '/receptie', icon: ClipboardCheck, module: 'stock', permission: 'stock.read' },
      { name: 'Comenzi furnizori', href: '/achizitii', icon: ShoppingCart, module: 'stock' },
      // Pictograma ANAF e-Factura înlocuiește Receipt + badge — branding mai
      // puternic, mai puțin noise vizual în sidebar.
      { name: 'Facturi primite', href: '/efactura', icon: EfacturaIcon, permission: 'efactura.read' },
      { name: 'Cheltuieli', href: '/cheltuieli', icon: Wallet, permission: 'expenses.read' },
      { name: 'Plăți furnizori', href: '/plati-furnizori', icon: CreditCard, permission: 'payments.read' },
      { name: 'Furnizori', href: '/furnizori', icon: Building2, permission: 'clients.read' },
      // „Import furnizori" mutat în Setări → Date.
    ],
  },
  // Stocuri rămâne ca grup propriu (bidirecțional — feeds both Vânzări & Achiziții).
  {
    name: 'Stocuri',
    icon: Warehouse,
    module: 'stock',
    defaultOpen: true,
    // Ordine în sidebar: prioritizat după frecvența de utilizare a Adela
    // (e-commerce LED retail multi-tenant). De la "zilnic activ" la "ocazional/
    // utilitar". Vezi conversație 2026-05-24.
    children: [
      // 1. Pagina cea mai vizitată zilnic — overview stoc curent
      { name: 'Stoc curent', href: '/stocuri', icon: Package, permission: 'stock.read' },
      // 2. Hub acțiuni operaționale (NIR / consum / transfer / inventariere)
      { name: 'Operații', href: '/stocuri/operatii', icon: ClipboardList, permission: 'stock.read' },
      // 3. Bulk import e-Factura → NIR — folosit aproape zilnic la reconciliere
      { name: 'Descărcare facturi', href: '/stocuri/descarcare-facturi', icon: PackageMinus, permission: 'stock.read' },
      // 4. Audit trail tranzacții
      { name: 'Mișcări', href: '/stocuri/rapoarte/miscari', icon: ArrowLeftRight, permission: 'stock.read' },
      // 5. Analiză zilnică DLC / aging
      { name: 'Vechime stoc', href: '/stocuri/vechime-stoc', icon: TrendingUp, permission: 'stock.read' },
      // 6. Rotație stoc conține DATE FINANCIARE SENSIBILE (rotație, ABC, COGS,
      // revenue contribution). Vizibil doar pentru roluri cu acces financial:
      // owner, admin, accountant. Operator/viewer/pos NU văd link-ul.
      { name: 'Rotație stoc', href: '/stocuri/rotatie-stoc', icon: RefreshCw, permission: 'stock.financial.view' },
      // 7. Hub unic pentru toate operațiile de lichidare stoc (loturi expirate /
      // casări / donații / raport 101). Folosit periodic (lunar/trimestrial).
      { name: 'Lichidare stocuri', href: '/stocuri/lichidare', icon: Recycle, permission: 'stock.adjust' },
      // 8. Hub rapoarte detaliate (balanță / predicție / fișa magazie /
      // diferențe furnizori / inventarieri centralizatoare etc.) — lunar/anual.
      { name: 'Rapoarte stocuri', href: '/stocuri/rapoarte', icon: BarChart3, permission: 'stock.read' },
      // 9. Utilitar imprimare etichete la cerere
      { name: 'Etichete cod de bare', href: '/stocuri/etichete', icon: Hash, permission: 'stock.read' },
      // 10. Scanner mobil (ascuns pe desktop via mobileOnly — vezi PR #200)
      { name: 'Scanner mobil', href: '/m/scanner', icon: Camera, permission: 'stock.adjust', badge: { text: 'Mobil', tone: 'neutral' }, mobileOnly: true },
    ],
  },
  // Grupul „Conturi" eliminat — Bancă + Trezorerie promovate top-level peer cu
  // Rapoarte/Contabilitate (industry standard Wave/Zoho/QBO: Banking never buried).
  { name: 'Rapoarte', href: '/rapoarte', icon: BarChart3, module: 'reports', permission: 'reports.read' },
  { name: 'Bancă', href: '/banca', icon: CreditCard, badge: { text: 'Beta', tone: 'amber' }, permission: 'bank_sync.read' },
  { name: 'Trezorerie', href: '/trezorerie', icon: Landmark, permission: 'bank_sync.read' },
  { name: 'Reconciliere', href: '/reconciliere', icon: ArrowLeftRight, module: 'reconciliation', permission: 'reconciliation.read' },
  {
    name: 'POS',
    icon: Monitor,
    module: 'pos',
    children: [
      { name: 'Terminal', href: '/pos', icon: Monitor, permission: 'pos.use' },
      { name: 'Restaurant', href: '/pos/floor', icon: UtensilsCrossed, permission: 'pos.use', badge: { text: 'Beta', tone: 'amber' } },
    ],
  },
  { name: 'Ride-sharing', href: '/transport-alternativ', icon: Car, permission: 'rideHailing.read' },
  { name: 'Contabilitate', href: '/contabilitate', icon: AccountingIcon, module: 'accounting', permission: 'accounting.read' },
  // REGES = item flat separat, sub Contabilitate (cerere owner 2026-07-21) — scos din grupul Achiziții.
  { name: 'REGES / Salariați', href: '/reges', icon: UserCheck, permission: 'reges.read' },
  // Nomenclatoarele au fost mutate în /setari (grupul „Nomenclatoare").
  // /nomenclatoare?tab=X redirectează la /setari?tab=X (bookmark-uri vechi).
  { name: 'Setări', href: '/setari', icon: Settings },
];

// ── Helper: find nav item by href ──────────────────

function findNavItem(href: string): NavItem | null {
  // Match exact pe arborele de navigare (top-level + children grupuri)
  for (const entry of navigation) {
    if (!isNavGroup(entry) && entry.href === href) return entry;
    if (isNavGroup(entry)) {
      const found = entry.children.find((c) => c.href === href);
      if (found) return found;
    }
  }
  // Fallback pentru URL-uri tab-specifice din /setari și /nomenclatoare
  // (sub-meniuri laterale care folosesc query string `?tab=`).
  // Resolvăm tab-id în NOMENCL_ITEMS sau ALL_NAV_ITEMS pentru icon+label
  // specific tab-ului. Returnăm un NavItem cu href-ul COMPLET (cu query)
  // pentru routing corect și matching în PinnedSection.
  if (href.includes('?tab=')) {
    const [pathname, query] = href.split('?');
    const tabId = new URLSearchParams(query).get('tab');
    if (tabId) {
      if (pathname === '/nomenclatoare') {
        const item = NOMENCL_ITEMS.find((i) => i.id === tabId);
        if (item) return { name: item.label, href, icon: item.icon, module: item.module };
      }
      if (pathname === '/setari') {
        const item = SETTINGS_NAV_ITEMS.find((i) => i.id === tabId);
        if (item) return { name: item.label, href, icon: item.icon };
      }
    }
  }
  return null;
}

// ── PRO Badge ──────────────────────────────────────

function ProBadge({ collapsed }: { collapsed?: boolean }) {
  if (collapsed) {
    return (
      <Puzzle className="h-3 w-3 text-amber-500" />
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
      <Puzzle className="h-2.5 w-2.5" />
      PRO
    </span>
  );
}

// ── Nav Badge — small pill next to a nav item label ──────────────────
// Mirrors the badge style from `settings-nav.tsx` (e-Factura "ANAF") so
// nav items in both surfaces feel consistent. Hidden when the sidebar
// is collapsed — collapsed state already drops labels, so a pill would
// have nothing to anchor to.

function NavBadge({ spec, collapsed }: { spec: NavBadgeSpec; collapsed?: boolean }) {
  if (collapsed) return null;
  // Variantă cu icon (ex e-Factura ANAF) — fără text, doar pictogramă pe
  // background subtle ca să nu domine vizual rândul nav. Aria-label rămâne
  // pentru screen readers.
  if (spec.icon) {
    const Icon = spec.icon;
    return (
      <span
        className="inline-flex items-center justify-center shrink-0 text-neutral-500 dark:text-neutral-400"
        aria-label={spec.text}
        title={spec.text}
      >
        <Icon className="h-4 w-auto" aria-hidden />
      </span>
    );
  }
  const toneClass = {
    neutral: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 border-neutral-200 dark:border-neutral-700',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-900',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-900',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-900',
  }[spec.tone || 'neutral'];
  return (
    <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide', toneClass)}>
      {spec.text}
    </span>
  );
}

// ── Cifra roșie: comenzi marketplace care așteaptă factură ──────────
// Calculată o dată în Sidebar (o singură cerere pentru toate platformele) și citită de grupuri
// și de „Fixate” prin context: href → număr. `pulse` = tocmai a sosit o comandă: cifra pâlpâie
// câteva secunde, apoi rămâne fixă până la facturare.

interface NavCounts {
  counts: Record<string, number>;
  /** Paginile unde tocmai a crescut numărul — pâlpâie câteva secunde. */
  pulsing: ReadonlySet<string>;
}

const NO_PULSE: ReadonlySet<string> = new Set();
const NavCountsContext = createContext<NavCounts>({ counts: {}, pulsing: NO_PULSE });

const ARRIVAL_PULSE_MS = 5000;

/**
 * Paginile al căror număr a crescut (și la prima încărcare, dacă sunt comenzi în așteptare),
 * timp de câteva secunde. Scăderea (o comandă facturată) oprește pâlpâirea.
 */
function useArrivalPulse(counts: Record<string, number>): ReadonlySet<string> {
  const key = Object.keys(counts).sort().map((h) => `${h}=${counts[h]}`).join('|');
  const prevRef = useRef<Record<string, number>>({});
  const [pulse, setPulse] = useState<{ hrefs: ReadonlySet<string>; until: number }>({ hrefs: NO_PULSE, until: 0 });
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = counts;
    const rose = Object.keys(counts).filter((h) => (counts[h] ?? 0) > (prev[h] ?? 0));
    if (rose.length) setPulse({ hrefs: new Set(rose), until: Date.now() + ARRIVAL_PULSE_MS });
    else if (Object.keys(prev).some((h) => (counts[h] ?? 0) < (prev[h] ?? 0))) setPulse({ hrefs: NO_PULSE, until: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` rezumă `counts` (obiect nou la fiecare randare)
  }, [key]);
  useEffect(() => {
    if (!pulse.until) return;
    const t = setTimeout(() => setPulse({ hrefs: NO_PULSE, until: 0 }), Math.max(0, pulse.until - Date.now()));
    return () => clearTimeout(t);
  }, [pulse.until]);
  return pulse.hrefs;
}

/** Bulina roșie cu numărul. `compact` = mai mică, poziționată de `className` peste o iconiță. */
function NavCountBadge({ count, pulse, compact, className }: { count: number; pulse?: boolean; compact?: boolean; className?: string }) {
  if (count <= 0) return null;
  const text = uninvoicedOrdersText(count);
  return (
    <span className={cn('inline-flex shrink-0', className ?? 'relative')} title={text}>
      {pulse && <span aria-hidden="true" className="absolute inset-0 rounded-full bg-red-500 animate-ping motion-reduce:hidden" />}
      <span
        aria-hidden="true"
        className={cn(
          'relative inline-flex items-center justify-center rounded-full bg-red-600 font-semibold leading-none text-white tabular-nums',
          compact ? 'h-4 min-w-[1rem] px-1 text-[0.5625rem]' : 'h-5 min-w-[1.25rem] px-1.5 text-[0.6875rem]',
        )}
      >
        {navCountLabel(count)}
      </span>
      <span className="sr-only">{text}</span>
    </span>
  );
}

// ── Helper: check if path matches ──────────────────

// Hrefs that are prefix of at least one other nav href must use exact-match
// only, otherwise they light up as active on any sub-page.
const STRICT_MATCH_HREFS = (() => {
  const all: string[] = [];
  for (const entry of navigation) {
    if (isNavGroup(entry)) for (const c of entry.children) all.push(c.href);
    else all.push(entry.href);
  }
  const set = new Set<string>();
  for (const h of all) {
    if (all.some((o) => o !== h && o.startsWith(h + '/'))) set.add(h);
  }
  return set;
})();

function isPathActive(href: string, pathname: string) {
  // The `pathname` param here is actually the FULL path (pathname + search, see
  // Sidebar where it's composed via usePathname() + useSearchParams()). That way
  // two items pointing to the same route with different queries (e.g.
  // /migrare?tip=furnizori vs /migrare?tip=produse-clienti) do NOT both light up.
  if (href.includes('?') || href.includes('#')) return pathname === href;
  const pathOnly = pathname.split('?')[0]!.split('#')[0]!;
  if (href === '/') return pathOnly === '/';
  if (STRICT_MATCH_HREFS.has(href)) return pathOnly === href;
  return pathOnly === href || pathOnly.startsWith(href + '/');
}

function isGroupActive(group: NavGroup, pathname: string) {
  return group.children.some((child) => isPathActive(child.href, pathname));
}

// ── Expandable Nav Item (chevron + sub-items dropdown) ─────────────

function ExpandableNavItem({
  item,
  pathname,
}: {
  item: NavItem;
  pathname: string;
}) {
  const subActive = item.children?.some((c) => isPathActive(c.href, pathname)) ?? false;
  const parentActive = isPathActive(item.href, pathname);

  // Default: closed pe orice pagină. Doar manual toggle (chevron) deschide dropdown.
  const [expanded, setExpanded] = useState(false);

  // Auto-collapse când URL-ul se schimbă (după click pe sub-item sau orice altă navigare).
  // Sidebar-ul e persistent în layout — fără asta state-ul ar rămâne deschis cross-navigare.
  useEffect(() => {
    setExpanded(false);
  }, [pathname]);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  const isActive = parentActive || subActive;

  return (
    <div>
      {/* Parent row: label = navigare, chevron = toggle */}
      <div
        className={cn(
          'flex items-center gap-2.5 rounded-lg pl-9 pr-1 py-1 text-sm transition-all duration-150',
          isActive
            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium shadow-sm'
            : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
        )}
      >
        <Link
          href={item.href}
          aria-current={parentActive ? 'page' : undefined}
          className="flex flex-1 items-center gap-2.5 min-w-0"
        >
          <item.icon className={cn('h-4 w-4 flex-shrink-0', isActive && 'text-primary-600 dark:text-primary-400')} />
          <span className="flex-1 truncate">{item.name}</span>
        </Link>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Restrânge submeniul' : 'Extinde submeniul'}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-neutral-200/60 dark:hover:bg-neutral-700/60 flex-shrink-0"
        >
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-200',
              expanded ? 'rotate-180' : 'rotate-0',
              isActive ? 'text-primary-600 dark:text-primary-400' : 'text-neutral-400',
            )}
          />
        </button>
      </div>

      {/* Sub-items — render condițional cu animație */}
      <div
        className="overflow-hidden transition-all duration-200 ease-in-out"
        style={{
          maxHeight: expanded ? `${(item.children?.length ?? 0) * 26 + 4}px` : 0,
          opacity: expanded ? 1 : 0,
        }}
      >
        <div className="mt-0.5 space-y-0.5">
          {item.children?.map((sub) => {
            const subItemActive = isPathActive(sub.href, pathname);
            return (
              <Link
                key={sub.href}
                href={sub.href}
                aria-current={subItemActive ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-2 rounded-md ml-7 pl-3 pr-2 py-0.5 text-[12.5px] transition-colors',
                  subItemActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium'
                    : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
                )}
              >
                <span className="flex-1 truncate">{sub.name}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Collapsible Group Component ────────────────────

function NavGroupItem({
  group,
  pathname,
  collapsed,
}: {
  group: NavGroup;
  pathname: string;
  collapsed: boolean;
}) {
  const isActive = isGroupActive(group, pathname);
  // Grup „efectiv" deschis: pin-ul turului are prioritate peste accordion-ul user.
  const isOpen = useSidebarGroupsStore((s) => selectEffectiveOpenGroup(s) === group.name);
  const toggleGroup = useSidebarGroupsStore((s) => s.toggleGroup);
  const childrenRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>('auto');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Position the collapsed-nav popover to the right of the trigger; clamp + cap so a
  // group with many items never spills past the viewport bottom on short screens.
  const popoverPos = useAnchoredPosition({ open: popoverOpen, anchorRef: triggerRef, floatingRef: popoverRef, placement: 'right-start', width: 208 });
  const { data: subStatus } = useSubscriptionStatus();
  // While data is still loading, don't lock anything (avoids flash of ProBadges)
  const activeModules = subStatus?.subscription?.addons as string[] | undefined;
  const isModuleLocked = (moduleCode?: string): boolean => !!moduleCode && !!activeModules && !activeModules.includes(moduleCode);
  const popoverTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // cifra roșie pe rândurile grupului; totalul lor apare pe antet când grupul e strâns
  const { counts: navCounts, pulsing } = useContext(NavCountsContext);
  const groupCount = group.children.reduce((sum, c) => sum + (navCounts[c.href] ?? 0), 0);
  const groupPulse = group.children.some((c) => pulsing.has(c.href));

  // Measure children height for smooth animation. Re-măsurat și la schimbarea conținutului: cifra
  // roșie apare după prima măsurare și poate rupe un rând pe două („Comenzi Trendyol 1”) — fără
  // asta, ultimul rând al grupului deschis ar fi tăiat de maxHeight. `collapsed` în deps: meniul
  // îngust demontează lista, iar la relărgire trebuie observată cea nouă. Înălțimea 0 (listă
  // demontată sau ascunsă) nu suprascrie ultima măsurare bună.
  useEffect(() => {
    const el = childrenRef.current;
    if (!el) return;
    const measure = () => {
      if (el.isConnected && el.scrollHeight > 0) setHeight(el.scrollHeight);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [group.children.length, collapsed]);

  // Collapsed sidebar: show popover on hover (portal + fixed position pentru a evita clipping-ul din overflow containers)
  const openPopover = () => {
    clearTimeout(popoverTimerRef.current);
    setPopoverOpen(true);
  };
  const schedulePopoverClose = () => {
    popoverTimerRef.current = setTimeout(() => setPopoverOpen(false), 150);
  };

  if (collapsed) {
    return (
      <>
        <div onMouseEnter={openPopover} onMouseLeave={schedulePopoverClose}>
          <button
            ref={triggerRef}
            className={cn(
              'flex w-full items-center justify-center rounded-lg px-2 py-1.5 transition-all duration-150',
              isActive
                ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
            )}
            title={group.name}
          >
            <span className="sr-only">{group.name}</span>
            <span className="relative">
              <group.icon className={cn('h-5 w-5 flex-shrink-0', isActive && 'text-primary-600 dark:text-primary-400')} />
              <NavCountBadge count={groupCount} pulse={groupPulse} compact className="absolute -right-2.5 -top-2" />
            </span>
          </button>
        </div>

        {popoverOpen && typeof window !== 'undefined' && createPortal(
          <div
            ref={popoverRef}
            style={{ position: 'fixed', top: popoverPos?.top ?? 0, left: popoverPos?.left ?? 0, zIndex: 60, maxHeight: popoverPos?.maxHeight, overflowY: 'auto', visibility: popoverPos ? 'visible' : 'hidden' }}
            className="w-52 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1.5 animate-fade-in"
            onMouseEnter={() => clearTimeout(popoverTimerRef.current)}
            onMouseLeave={schedulePopoverClose}
          >
            <div className="px-3 py-1.5 text-xs font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
              {group.name}
            </div>
            {group.children.map((child) => {
              const childActive = isPathActive(child.href, pathname);
              return (
                <Link
                  key={child.href}
                  href={child.href}
                  data-tour={tourAnchorFor(child.href)}
                  aria-current={childActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-1 text-sm transition-colors',
                    childActive
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
                  )}
                >
                  <child.icon className={cn('h-4 w-4', childActive && 'text-primary-600 dark:text-primary-400')} />
                  <span className="flex-1">{child.name}</span>
                  <NavCountBadge count={navCounts[child.href] ?? 0} pulse={pulsing.has(child.href)} />
                  {child.badge && <NavBadge spec={child.badge} />}
                  {isModuleLocked(child.module) && <ProBadge />}
                </Link>
              );
            })}
          </div>,
          document.body
        )}
      </>
    );
  }

  // Expanded sidebar: collapsible group
  return (
    <div>
      {/* Group header */}
      <button
        onClick={() => toggleGroup(group.name)}
        data-tour={groupTourAnchor(group.name)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-1 text-sm font-medium transition-all duration-150',
          isActive
            ? 'text-primary-700 dark:text-primary-300'
            : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
        )}
      >
        <group.icon className={cn('h-5 w-5 flex-shrink-0', isActive && 'text-primary-600 dark:text-primary-400')} />
        <span className="flex-1 text-left">{group.name}</span>
        {!isOpen && <NavCountBadge count={groupCount} pulse={groupPulse} />}
                {isModuleLocked(group.module) && <ProBadge />}
        <ChevronDown
          className={cn(
            'h-4 w-4 flex-shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {/* Children with smooth animation */}
      <div
        className="overflow-hidden transition-all duration-200 ease-in-out"
        style={{
          maxHeight: isOpen ? (typeof height === 'number' ? height + 8 : 'none') : 0,
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div ref={childrenRef} className="mt-0.5 space-y-0.5">
          {group.children.map((child) => {
            const childActive = isPathActive(child.href, pathname);
            // Primary CTA — full-width accent button, distinct vizual de nav rows
            if (child.primary) {
              return (
                <Link
                  key={child.href}
                  href={child.href}
                  data-tour={tourAnchorFor(child.href)}
                  className="mx-2 mb-1 flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <child.icon className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={2.5} />
                  <span>{child.name}</span>
                </Link>
              );
            }
            // Expandable — chevron toggle + sub-items dropdown (e.g. Documente Emise → 4 tipuri)
            if (child.expandable && child.children?.length) {
              return (
                <ExpandableNavItem
                  key={child.href}
                  item={child}
                  pathname={pathname}
                />
              );
            }
            return (
              <Link
                key={child.href}
                href={child.href}
                data-tour={tourAnchorFor(child.href)}
                aria-current={childActive ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg pl-9 pr-3 py-1 text-sm transition-all duration-150',
                  childActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium shadow-sm'
                    : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
                )}
              >
                <child.icon className={cn('h-4 w-4 flex-shrink-0', childActive && 'text-primary-600 dark:text-primary-400')} />
                <span className="flex-1">{child.name}</span>
                <NavCountBadge count={navCounts[child.href] ?? 0} pulse={pulsing.has(child.href)} />
                {child.badge && <NavBadge spec={child.badge} />}
                {isModuleLocked(child.module) && <ProBadge />}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Sidebar Search Trigger ─────────────────────────

function SidebarSearchTrigger({ collapsed }: { collapsed: boolean }) {
  const openPalette = useCallback(() => {
    window.dispatchEvent(new CustomEvent('open-command-palette'));
  }, []);

  if (collapsed) {
    return (
      <div className="flex flex-col items-center px-2 pt-3 gap-1">
        <Link
          href="/"
          title="Dashboard"
          aria-label="Dashboard"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10 transition-colors"
        >
          <LayoutDashboard className="h-6 w-6" strokeWidth={2.5} />
        </Link>
        <button
          onClick={openPalette}
          title="Caută (⌘K)"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
        >
          <Search className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 pt-3 flex items-center gap-2">
      <Link
        href="/"
        title="Dashboard"
        aria-label="Dashboard"
        className="flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10 transition-colors flex-shrink-0"
      >
        <LayoutDashboard className="h-6 w-6" strokeWidth={2.5} />
      </Link>
      <button
        onClick={openPalette}
        className="group flex flex-1 min-w-0 items-center gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400 hover:border-neutral-300 dark:hover:border-neutral-600 hover:bg-white dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200 transition-all"
      >
        <Search className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="flex-1 text-left truncate">Caută meniu…</span>
      </button>
    </div>
  );
}

// ── Pinned Section ─────────────────────────────────

// URL-urile CTA permanent vizibile sus — exclude din FIXATE pentru a nu duplica.
const PERMANENT_CTA_HREFS_SET = new Set(['/facturi/nou', '/cheltuieli/nou', '/receptie/nou']);

function PinnedSection({ pathname, collapsed }: { pathname: string; collapsed: boolean }) {
  const pinnedHrefs = useSidebarPrefsStore((s) => s.pinnedHrefs);
  const togglePin = useSidebarPrefsStore((s) => s.togglePin);
  const { data: subStatus } = useSubscriptionStatus();
  const activeModules = (subStatus?.subscription?.addons || []) as string[];
  const { shouldShowStock, isLoading: activityLoading } = useActivityType();
  const { data: companyData } = useCompany();
  const hideTrezorerie = companyData?.hasTreasuryPresence === false;
  // Ride-sharing: același tratament (audit 2026-07-12) — activarea rămâne în Setări → Integrări.
  const hideRideSharing = companyData?.hasRideHailingPresence === false;
  const hidePos = companyData?.hasPosPresence === false; // audit 2026-07-12 — activare: Setări → Integrări → POS & Casă
  const { can } = usePermissions();
  const { counts: navCounts, pulsing } = useContext(NavCountsContext);


  if (pinnedHrefs.length === 0) return null;

  // Filter out CTA URLs care apar deja ca buton sus — prevenim duplicarea
  // dacă utilizatorul are pin-uri vechi (din vremea când pinning-ul era permis).
  // Paginile restricționate (ex. creările, pentru un cont doar de citire) apar doar cu dreptul lor.
  let visibleHrefs = pinnedHrefs.filter((href) => !PERMANENT_CTA_HREFS_SET.has(href) && canOpenPath(href, can));
  // Activity-type gate: tenants services nu vad stoc-related items in Fixate
  if (!activityLoading && !shouldShowStock) {
    visibleHrefs = visibleHrefs.filter((href) =>
      href !== "/receptie" && href !== "/receptie/nou" && href !== "/achizitii" && !href.startsWith("/stocuri")
    );
  }
  // «Borderouri curieri» pinat: aceeași regulă ca în navigație (fără profil
  // servicii și fără tenants cu modulul stock inactiv). subStatus prezent =
  // subscripția e încărcată (addons goale ≠ loading).
  if ((!activityLoading && !shouldShowStock) || (!!subStatus && !activeModules.includes('stock'))) {
    visibleHrefs = visibleHrefs.filter((href) => href !== '/borderou-curier');
  }
  // Trezorerie pinată: aceeași regulă ca în navigație (fără legătură cu Trezoreria).
  if (hideTrezorerie) {
    visibleHrefs = visibleHrefs.filter((href) => href !== '/trezorerie');
  }
  if (hideRideSharing) {
    visibleHrefs = visibleHrefs.filter((href) => href !== '/transport-alternativ');
  }
  if (hidePos) {
    visibleHrefs = visibleHrefs.filter((href) => href !== '/pos' && !href.startsWith('/pos/'));
  }
  const items = visibleHrefs.map((href) => findNavItem(href)).filter((v): v is NavItem => v !== null);
  if (items.length === 0) return null;

  if (collapsed) {
    return (
      <div className="px-2 py-2 flex-shrink-0 border-t border-neutral-100 dark:border-neutral-800">
        <div className="space-y-0.5 pt-1">
          {items.map((item) => {
            const isActive = isPathActive(item.href, pathname);
            return (
              <Link
                key={item.href}
                href={item.href}
                title={'★ ' + item.name}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex items-center justify-center rounded-lg px-2 py-2 transition-colors relative',
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-amber-600 dark:text-amber-400 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                )}
              >
                <span className="sr-only">{item.name}</span>
                <span className="relative">
                  <item.icon className="h-5 w-5" />
                  {/* jos: colțul de sus e al pionezei */}
                  <NavCountBadge count={navCounts[item.href] ?? 0} pulse={pulsing.has(item.href)} compact className="absolute -right-2.5 -bottom-2" />
                </span>
                <Pin className="absolute top-1 right-1 h-2 w-2 fill-amber-500 text-amber-500" />
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 py-2 flex-shrink-0">
      <div className="mx-2 mb-2 border-t border-neutral-100 dark:border-neutral-800" />
      <div
        className="flex items-center gap-1 px-3 pb-1.5 text-[0.625rem] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
        aria-label={`Fixate, ${items.length} din 5`}
      >
        <span>Fixate</span>
        <span aria-hidden="true" className="ml-auto text-[0.5625rem] font-normal normal-case tracking-normal text-neutral-400">
          {items.length}/5
        </span>
      </div>
      <div className="space-y-0.5">
        {items.map((item) => {
          const isActive = isPathActive(item.href, pathname);
          const locked = !!item.module && !activeModules.includes(item.module);
          return (
            <div key={item.href} className="group relative">
              <Link
                href={item.href}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm transition-all',
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium'
                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100'
                )}
              >
                <item.icon
                  className={cn(
                    'h-4 w-4 flex-shrink-0',
                    isActive && 'text-primary-600 dark:text-primary-400'
                  )}
                />
                <span className="flex-1 truncate">{item.name}</span>
                <NavCountBadge count={navCounts[item.href] ?? 0} pulse={pulsing.has(item.href)} />
                {locked && <ProBadge />}
              </Link>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePin(item.href);
                }}
                title="Scoate de la fixate"
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded text-amber-500 opacity-0 hover:bg-amber-50 dark:hover:bg-amber-900/20 group-hover:opacity-100 transition-opacity"
              >
                <PinOff className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Sidebar Component ─────────────────────────

export function Sidebar() {
  const _pathname = usePathname();
  const _search = useSearchParams();
  // Compose a single string that includes BOTH the route AND the query so
  // isPathActive can distinguish two items sharing a route (e.g. /migrare?tip=...).
  const pathname = _search?.toString()
    ? `${_pathname}?${_search.toString()}`
    : _pathname;
  const [collapsed, setCollapsed] = useState(false);
  const { isOpen, close } = useSidebar();
  const { data: subStatus } = useSubscriptionStatus();
  // While data is still loading, don't lock anything (avoids flash of ProBadges)
  const activeModules = subStatus?.subscription?.addons as string[] | undefined;
  const { hasAccess: hasStockAccess } = useUserHasWarehouseAccess();
  const { shouldShowStock, isLoading: activityLoading } = useActivityType();
  const user = useAuthStore((s) => s.user);
  // User permissions pentru filtrare per-link (separat de module gating).
  const userPerms = new Set(user?.permissions || []);
  // Mobile/tablet detection: pe desktop (≥lg = 1024px) ascundem item-urile
  // marcate mobileOnly (ex. Scanner mobil care e dedicat tactil).
  const isMobileOrTablet = useIsMobile(1024);
  const isAllowedOnViewport = (item: NavItem): boolean => !item.mobileOnly || isMobileOrTablet;
  // Butoanele mari de creare (cta) urmează harta adreselor: o adresă poate cere mai multe drepturi (ex. „Cheltuială
  // nouă” = crearea cheltuielii + lista /cheltuieli, unde stă fereastra).
  const hasPermFor = (item: NavItem): boolean =>
    (!item.permission || userPerms.has(item.permission as any))
    && (!(item.cta || item.primary) || canOpenPath(item.href, (p) => userPerms.has(p)));

  // While activity type is loading, treat as "show all" to avoid flicker.
  // For services tenants, the layout-level gate on /receptie /stocuri /achizitii
  // still blocks access if user navigates fast — defense-in-depth.
  const hideStockUI = !activityLoading && !shouldShowStock;
  const isStockHref = (href: string | undefined) =>
    href === '/receptie' || href === '/receptie/nou' || href === '/achizitii';
  // «Borderouri curieri» = flux de expediere marfă → ascuns complet pentru
  // profilul «doar servicii» sau când modulul de gestiune (stock) nu e activ.
  // activeModules undefined = subscripția încă se încarcă → nu ascundem.
  const hideCourierManifests =
    hideStockUI || (activeModules !== undefined && !activeModules.includes('stock'));

  // Trezorerie: vizibil doar dacă firma are legătură cu Trezoreria Statului (IBAN TREZ,
  // certificat SPV activ sau extrase importate — flag hasTreasuryPresence din
  // /companies/current; audit 2026-07-12). Pattern identic cu marketplace-urile ascunse
  // până la conectare. companyData undefined (loading / cache vechi fără flag) = nu ascundem.
  const { data: companyData } = useCompany();
  const hideTrezorerie = companyData?.hasTreasuryPresence === false;
  // Ride-sharing: același tratament (audit 2026-07-12) — activarea rămâne în Setări → Integrări.
  const hideRideSharing = companyData?.hasRideHailingPresence === false;
  const hidePos = companyData?.hasPosPresence === false; // audit 2026-07-12 — activare: Setări → Integrări → POS & Casă


  // Marketplace cu afișare condiționată de conexiune (eMAG + Fashion Days — ascunse
  // până la conectarea contului, cerință user). Interogăm DOAR dacă userul ar
  // putea vedea item-ul (are invoicing_pro + invoices.read) — altfel /marketplace
  // răspunde 402 (modul) și ar genera request inutil pentru toți.
  const wantsMarketplace = !!activeModules?.includes('invoicing_pro') && userPerms.has('invoices.read');
  const { data: emagSettings } = useMarketplaceSettings('emag', wantsMarketplace);
  const { data: fdSettings } = useMarketplaceSettings('fashiondays', wantsMarketplace);
  const { data: tySettings } = useMarketplaceSettings('trendyol', wantsMarketplace);
  const connectedMarketplaces = new Set<string>();
  if (emagSettings?.connected) connectedMarketplaces.add('emag');
  if (fdSettings?.connected) connectedMarketplaces.add('fashiondays');
  if (tySettings?.connected) connectedMarketplaces.add('trendyol');
  const isConnectedMarketplace = (item: NavItem): boolean =>
    !item.requiresConnectedMarketplace || connectedMarketplaces.has(item.requiresConnectedMarketplace);
  // Cifra roșie lângă „Comenzi eMAG” / „Comenzi Trendyol”…: comenzile care așteaptă factură —
  // doar pentru cine le poate factura (un rol doar de citire n-ar avea cum s-o stingă).
  const canInvoiceOrders = wantsMarketplace && userPerms.has('invoices.create');
  const { data: uninvoiced } = useMarketplaceUninvoicedCounts(canInvoiceOrders && connectedMarketplaces.size > 0);
  const navCounts = canInvoiceOrders ? uninvoicedNavCounts(uninvoiced?.counts, connectedMarketplaces) : {};
  const pulsing = useArrivalPulse(navCounts);

  const filteredNavigation = navigation
    .filter((entry) => {
      // Ascunde item-urile top-level pe care rolul userului nu le poate accesa
      // (ex. Contabilitate fara accounting.read). Copiii grupurilor sunt deja filtrati mai jos.
      if (!isNavGroup(entry) && !hasPermFor(entry)) return false;
      if (isNavGroup(entry) && entry.name === 'Stocuri' && !hasStockAccess) return false;
      // Activity-type gate: tenants `services` nu vad stoc/NIR/achizitii
      if (hideStockUI) {
        if (isNavGroup(entry) && entry.name === 'Stocuri') return false;
        if (!isNavGroup(entry)) {
          const href = (entry as any).href as string | undefined;
          if (isStockHref(href)) return false;
        }
      }
      if (hideCourierManifests && !isNavGroup(entry) && (entry as any).href === '/borderou-curier') return false;
      // Trezorerie: ascuns pentru firmele fără legătură cu Trezoreria Statului.
      if (hideTrezorerie && !isNavGroup(entry) && (entry as any).href === '/trezorerie') return false;
      if (hideRideSharing && !isNavGroup(entry) && (entry as any).href === '/transport-alternativ') return false;
      // POS: grup întreg ascuns pentru firmele fără activitate de ghișeu (precedent: grupul Stocuri).
      if (hidePos && isNavGroup(entry) && entry.name === 'POS') return false;
      return true;
    })
    .map((entry) => {
      // Filtrăm copiii grupurilor după permisiuni (ex. „Rotație stoc"
      // hidden pentru operator/viewer fără stock.financial.view) și după
      // activity type (ex. „Recepții (NIR)" sub Achiziții).
      if (isNavGroup(entry)) {
        let allowedChildren = entry.children.filter((c) => hasPermFor(c) && isAllowedOnViewport(c) && isConnectedMarketplace(c));
        if (hideStockUI) {
          allowedChildren = allowedChildren.filter((c) => !isStockHref(c.href));
        }
        if (hideCourierManifests) {
          allowedChildren = allowedChildren.filter((c) => c.href !== '/borderou-curier');
        }
        // Ascundem grupul complet dacă niciun copil nu e permis (ex. POS pt roluri
        // fără pos.use) — altfel rămâne un header gol, clicabil.
        if (allowedChildren.length === 0) return null;
        return { ...entry, children: allowedChildren };
      }
      // Nav items top-level cu permission required + viewport gate
      if (!hasPermFor(entry)) return null;
      if (!isAllowedOnViewport(entry)) return null;
      if (!isConnectedMarketplace(entry)) return null;
      return entry;
    })
    .filter((entry): entry is NavigationEntry => entry !== null);

  const isModuleLocked = (moduleCode?: string): boolean => {
    if (!moduleCode) return false;
    if (!activeModules) return false; // data not loaded yet — don't lock
    return !activeModules.includes(moduleCode);
  };

  // Starea grupurilor accordion trăiește într-un store Zustand modul-scoped
  // (sidebar-groups-store) ca să supraviețuiască re-mount-ului sidebar-ului
  // (refreshProfile după restart tur). Turul „pinează" grupul-țintă acolo.
  // La mount, curăță un openGroup persistat care nu mai există în navigație (anti-stale).
  useEffect(() => {
    useSidebarGroupsStore
      .getState()
      .validateAgainst(navigation.filter(isNavGroup).map((g) => g.name));
  }, []);

  // Auto-open grupul paginii active la navigare. Delegă la store: syncActiveGroup
  // e NO-OP cât timp turul pinează un grup → reconcilierea nu mai poate colapsa
  // grupul-țintă al turului (nici măcar la un re-mount al sidebar-ului).
  const prevPathnameRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    const activeEntry = filteredNavigation.find(
      (entry) => isNavGroup(entry) && isGroupActive(entry, pathname)
    );
    useSidebarGroupsStore
      .getState()
      .syncActiveGroup(activeEntry && isNavGroup(activeEntry) ? activeEntry.name : null);
  }, [pathname]);

  // Sidebar collapsed (icon-rail) togglabil din top-bar / tur via eveniment.
  useEffect(() => {
    function onSetCollapsed(e: Event) {
      const ev = e as CustomEvent<{ collapsed?: boolean }>;
      if (typeof ev.detail?.collapsed === 'boolean') setCollapsed(ev.detail.collapsed);
    }
    window.addEventListener('sf-sidebar:set-collapsed', onSetCollapsed);
    return () => {
      window.removeEventListener('sf-sidebar:set-collapsed', onSetCollapsed);
    };
  }, []);

  // Notify other components (TopBar etc.) about collapsed state changes,
  // so they can render an expand button in their own area when sidebar is
  // collapsed.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('sf-sidebar:state-changed', { detail: { collapsed } }));
  }, [collapsed]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    close();
  }, [pathname, close]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const sidebarContent = (
    <>
      {/* Header: Logo + Collapse toggle */}
      <div className="border-b border-neutral-200 dark:border-neutral-700">
        {!collapsed ? (
          <div className="flex h-16 items-center justify-between gap-2 px-4">
            <Link href="/" className="flex items-center min-w-0">
              <BrandLogoFull />
            </Link>
            {/* Desktop collapse toggle */}
            <button
              onClick={() => setCollapsed(true)}
              className="hidden lg:inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 hover:text-primary hover:bg-primary/5 transition-colors shrink-0"
              aria-label="Restrânge meniul"
              title="Restrânge meniul"
            >
              <PanelLeftClose className="h-[18px] w-[18px]" />
            </button>
            {/* Mobile close */}
            <button
              onClick={close}
              className="lg:hidden flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors shrink-0"
              aria-label="Închide meniul"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex h-16 items-center justify-center px-2">
            <Link href="/" className="flex items-center" title="SoftFactura">
              <BrandLogoIcon className="text-primary" size={28} />
            </Link>
            {/* Toggle-ul de expand este randat in TopBar (vezi top-bar.tsx) — apare
                in bara header langa hamburger, vizibil doar cand sidebar e colapsat. */}
          </div>
        )}
      </div>

      {/* Search trigger — deschide Cmd+K palette */}
      <SidebarSearchTrigger collapsed={collapsed} />

      {/* CTAs principale (cta: primary/secondary/tertiary) — ÎNTOTDEAUNA primele,
          sus, deasupra FIXATE. Acces 1-click la create flows fără scroll.
          Expanded: grid 3 coloane cu tile-uri (icon top, label centered, max 2 linii).
          Collapsed: stack vertical icon-only (păstrat din format-ul anterior). */}
      {!collapsed ? (
        <div className="flex gap-1.5 px-2 pt-3">
          {filteredNavigation
            .filter((entry): entry is NavItem => !isNavGroup(entry) && !!((entry as NavItem).cta || (entry as NavItem).primary))
            .map((entry) => {
              const ctaVariant = entry.cta ?? (entry.primary ? 'primary' : 'primary');
              // 3-level hierarchy: primary (solid orange) > secondary (outline orange)
              // > tertiary (outline neutral). În format tile, fiecare nivel are
              // tratament distinct de focus pentru scan vizual rapid din colțul ochiului.
              const ctaCls =
                ctaVariant === 'primary'
                  ? 'bg-primary text-white shadow-sm hover:bg-primary-700 hover:shadow-md ring-1 ring-primary/10'
                  : ctaVariant === 'secondary'
                  ? 'border border-primary/70 text-primary bg-white dark:bg-neutral-900 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-primary'
                  : 'border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-600';
              return (
                <Link
                  key={entry.name}
                  href={entry.href}
                  onClick={() => useSidebarGroupsStore.getState().closeAllGroups()}
                  // flex-1 + basis-0: distribuție uniformă chiar și când un CTA e
                  // ascuns de module-gate (ex: stock lipsă → 2 tile-uri 50/50).
                  className={cn(
                    'group relative flex flex-1 basis-0 min-w-0 aspect-[1.05/1] flex-col items-center justify-center gap-1.5 rounded-lg px-1 py-2 text-[11px] font-medium leading-tight transition-all focus:outline-none focus:ring-2 focus:ring-primary/40',
                    ctaCls,
                  )}
                >
                  <entry.icon
                    className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110"
                    strokeWidth={2}
                  />
                  <span className="text-center whitespace-normal line-clamp-2">{entry.name}</span>
                </Link>
              );
            })}
        </div>
      ) : (
        <div className="px-1 pt-3 space-y-1">
          {filteredNavigation
            .filter((entry): entry is NavItem => !isNavGroup(entry) && !!((entry as NavItem).cta || (entry as NavItem).primary))
            .map((entry) => {
              const ctaVariant = entry.cta ?? (entry.primary ? 'primary' : 'primary');
              const ctaCls =
                ctaVariant === 'primary'
                  ? 'bg-primary text-white shadow-sm hover:bg-primary-700'
                  : ctaVariant === 'secondary'
                  ? 'border border-primary text-primary bg-white dark:bg-neutral-900 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                  : 'border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800';
              return (
                <Link
                  key={entry.name}
                  href={entry.href}
                  onClick={() => useSidebarGroupsStore.getState().closeAllGroups()}
                  title={entry.name}
                  className={cn(
                    'flex items-center justify-center rounded-md px-2 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40',
                    ctaCls,
                  )}
                >
                  <entry.icon className="h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                </Link>
              );
            })}
        </div>
      )}

      {/* Navigation — restul (groups + non-CTA top-level items).
          Fixate-le mutate jos după Setări (vezi blocul post-</nav>). */}
      <nav className="flex-1 space-y-0.5 px-1 pt-3 pb-2 overflow-y-auto">
        {filteredNavigation.map((entry) => {
          if (isNavGroup(entry)) {
            return (
              <NavGroupItem
                key={entry.name}
                group={entry}
                pathname={pathname}
                collapsed={collapsed}
              />
            );
          }

          // CTA items sunt randate sus (deasupra FIXATE) — skip aici ca să
          // nu apară de 2 ori. Vezi blocul „CTAs principale" mai sus.
          const navItem = entry as NavItem;
          if (navItem.cta || navItem.primary) {
            return null;
          }
          const isActive = isPathActive(entry.href, pathname);
          // Collapsed mode — icon-only, custom centering + ProBadge overlay.
          // Out of NavLeafItem scope (label-less).
          if (collapsed) {
            return (
              <Link
                key={entry.name}
                href={entry.href}
                onClick={() => useSidebarGroupsStore.getState().closeAllGroups()}
                className={cn(
                  'flex items-center gap-3 rounded-lg py-1.5 text-sm font-medium transition-all duration-150 justify-center px-2 relative',
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
                )}
                title={entry.name}
              >
                <entry.icon className={cn('h-5 w-5 flex-shrink-0', isActive && 'text-primary-600 dark:text-primary-400')} />
                {isModuleLocked((entry as NavItem).module) && <span className="absolute right-1 top-1"><ProBadge collapsed /></span>}
              </Link>
            );
          }
          // Expanded mode — standard leaf item, NavLeafItem shared shape.
          return (
            <NavLeafItem
              key={entry.name}
              href={entry.href}
              onClick={() => useSidebarGroupsStore.getState().closeAllGroups()}
              icon={entry.icon}
              label={entry.name}
              active={isActive}
              truncate={false}
              rightSlot={isModuleLocked((entry as NavItem).module) ? <ProBadge /> : undefined}
            />
          );
        })}
      {/* Admin Panel link — only for super admins */}
      {user?.isSuperAdmin && (
        <div className="mt-2 px-2 pb-2">
          <Link
            href="/admin"
            className={cn(
              'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
              pathname.startsWith('/admin')
                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
              collapsed && 'justify-center px-2',
            )}
            title={collapsed ? 'Admin Panel' : undefined}
          >
            <Shield className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span>Admin Panel</span>}
          </Link>
        </div>
      )}

      </nav>

      {/* Pinned section — afișat jos după Setări, ca shortcut secundar la
          paginile frecvent accesate (alternativ la Cmd+K).
          Self-rendering wrapper + separator INTERN (return null când nu există
          items vizibile — evităm spațiu fantomă cu separator). */}
      <PinnedSection pathname={pathname} collapsed={collapsed} />

      {/* Buton suport in footer — randat doar daca FAB-ul e ascuns. */}
      <SidebarSupportButton collapsed={collapsed} />
    </>
  );

  return (
    <NavCountsContext.Provider value={{ counts: navCounts, pulsing }}>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden animate-fade-in"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-white dark:bg-neutral-900 shadow-xl dark:shadow-neutral-900/50 transition-transform duration-300 lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        data-tour-scope="desktop"
        className={cn(
          'hidden lg:flex h-screen flex-col border-r border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 transition-all duration-200',
          collapsed ? 'w-16' : 'w-56',
        )}
      >
        {sidebarContent}
      </aside>
    </NavCountsContext.Provider>
  );
}
